/**
 * Replace-logo backend. Two services, one flow.
 *
 * Replacing a logo is not a save — it is a pipeline run. The owner's file has to become a
 * URL the branding pipeline can fetch before that pipeline can be asked to rebuild anything
 * from it, so a replacement is always two calls:
 *
 *   1. {@link uploadArtwork}  — spark-ai stores the file and returns a permanent CDN URL.
 *   2. {@link queueLogoRun}   — casino-creation queues a targeted run that seeds one logo
 *                               from that URL and regenerates whatever derives from it.
 *
 * The run is ASYNCHRONOUS: it answers `202`-style with a `pipeline_id` and the new artwork
 * lands seconds to minutes later, so the caller polls {@link fetchLogoFamily} until the
 * family changes. Nothing here waits for it — see `./logo-replacement` for the state machine.
 *
 * WHY THE PROXY. Neither service is reachable from the browser. Both are called through the
 * owner-hub MLP proxy (`utils/mlp-proxy`), which authenticates with the owner's own token.
 * This is what makes replacement work on a LAUNCHED brand: the art routes are otherwise
 * reached with a deploy `session_id`, and a launched brand's session is terminal, so it
 * answers 401. The proxy carries owner auth instead of a session, so there is no session
 * to be expired.
 *
 * SCOPES DIFFER, and it matters. spark-ai is a shared service deployed next to the
 * owner-hub backend, NOT in every brand cluster — so the upload uses general scope (`g`),
 * a direct call from the owner-hub's own cluster. casino-creation is the opposite: it is
 * per-cluster and only knows its own tenants, so its calls use cluster scope (`c`) and are
 * forwarded to the brand's destination cluster. (On test the two clusters coincide, which
 * is why `c` for the upload appeared to work there; on prod the brand clusters have no
 * spark-ai and `c` uploads go nowhere.)
 */
import axios from 'axios';

import { createMlpProxyTransport } from '../../utils/mlp-proxy';

/* ── transports ──────────────────────────────────────────────────────────────
 * `api` = spark-ai. Its proxy entry points at the service ROOT, and spark-ai serves
 * everything under `/api/v1`, so the prefix must be added here. General scope: spark-ai
 * lives in the owner-hub's cluster, not the brand's (see module header). `cc` =
 * casino-creation (no prefix; routes live at `/casino/...`), cluster scope. Both opt out
 * of the auth interceptor's teardown via the shared transport: a downstream 401 is that
 * service's answer, not an owner-hub session verdict. */
const art = createMlpProxyTransport({
  svc: 'api',
  scope: 'g',
  pathPrefix: '/api/v1',
  headers: { Accept: 'application/json' },
});

const casinoCreation = createMlpProxyTransport({
  svc: 'cc',
  scope: 'c',
  headers: { Accept: 'application/json' },
});

/** True when the owner-hub proxy base is configured — nothing below can work without it. */
export const logoApiConfigured = (): boolean => art.configured() && casinoCreation.configured();

/* ── 1. upload the owner's artwork ───────────────────────────────────────────── */

/** What spark-ai answers for a stored image; only `cdn_url` matters downstream. */
type UploadResponse = {
  data?: { success?: boolean; image?: { cdn_url?: string; width?: number; height?: number } };
};

/**
 * Store the owner's picked file and return its permanent CDN URL.
 *
 * `POST /art/logo/upload` takes the image as a data URL (or raw base64) in a JSON body —
 * NOT multipart — and rasterizes SVG to PNG on the way in. The image is stored verbatim:
 * no background removal, no crop. Cleaning it up is the pipeline's job and is controlled by
 * `prepareUpload` on the run below, so the same upload serves both answers.
 */
export const uploadArtwork = async (
  imageDataUri: string,
  brandName: string,
  signal?: AbortSignal,
): Promise<string> => {
  const response = await axios.post<UploadResponse['data']>(
    art.url('/art/logo/upload'),
    { image_base64: imageDataUri, brand_name: brandName },
    art.requestConfig(signal),
  );
  const cdnUrl = response.data?.image?.cdn_url;
  if (!cdnUrl) throw new Error('The upload did not return a stored image');
  return cdnUrl;
};

/* ── 2. queue the run ────────────────────────────────────────────────────────── */

/** Slot id of the base logo. It is not a targetable variant: replacing it is a rebrand. */
export const PRIMARY_SLOT = 'symbol_and_name_horizontal';

/**
 * Slot id of the favicon: the graph's `favicon_key`, so its run is `logo_family:favicon` like any other variant's.
 * The family keeps its artwork beside the per-slot map rather than in it (`logo_assets.favicon`), and the
 * graph reports it through `favicon_changed` rather than in `changed`; see `./logos` and {@link runAffects}.
 */
export const FAVICON_SLOT = 'favicon';

export type QueueLogoRunOptions = {
  tenantId: number;
  /** Slot being replaced. `PRIMARY_SLOT` switches the run to the rebrand selector. */
  slot: string;
  /** CDN URL from {@link uploadArtwork} — the artwork this run seeds the slot with. */
  uploadUrl: string;
  /**
   * Let Lumio condition the file. On a variant that is background removal + a tight crop;
   * on the primary it additionally redraws the artwork into the canonical lockup. Off
   * stores the file exactly as the owner supplied it.
   */
  prepareUpload: boolean;
  /**
   * Rebuild everything derived from this slot. Off replaces the slot alone and leaves its
   * derived variants pointing at the old artwork — the backend then reports them as stale.
   */
  cascade: boolean;
};

export type QueuedRun = { pipelineId: string; tenantId: number; selector: string };

/**
 * Ask casino-creation to rebuild the family, seeding one slot from the uploaded artwork.
 *
 * The primary logo is the root every other asset derives from, so it has its own selector
 * (`logo_rebrand`); every other slot is named directly (`logo_family:<slot>`). Both accept
 * the same `uploadUrl` / `cascade` / `prepareUpload` answers, so the caller does not branch.
 *
 * Returns as soon as the run is QUEUED. The logos have not changed yet.
 */
export const queueLogoRun = async (
  { tenantId, slot, uploadUrl, prepareUpload, cascade }: QueueLogoRunOptions,
  signal?: AbortSignal,
): Promise<QueuedRun> => {
  const selector = slot === PRIMARY_SLOT ? 'logo_rebrand' : `logo_family:${slot}`;
  const response = await axios.post<{ pipeline_id?: string }>(
    casinoCreation.url('/casino/run', tenantId),
    { tenantId, mode: 'deploy', selector, uploadUrl, prepareUpload, cascade },
    casinoCreation.requestConfig(signal),
  );
  const pipelineId = response.data?.pipeline_id;
  if (!pipelineId) throw new Error('The run was not queued');
  return { pipelineId, tenantId, selector };
};

/* ── 3. read the family back ─────────────────────────────────────────────────── */

/** One asset in the live family: what the site renders for that slot. */
export type FamilyAsset = { src?: string; alt?: string; width?: number; height?: number };

export type LogoFamily = {
  tenant_id: number;
  brand_name?: string;
  logo_assets: {
    logos?: Record<string, FamilyAsset | undefined>;
    favicon?: { src?: string; alt?: string; source_asset?: string } | null;
  } | null;
  /**
   * Slot id → who made it. An owner's own artwork is one of the graph's
   * `owner_upload_methods`; anything else is Lumio's work. See {@link isOwnerArtwork}.
   */
  provenance: Record<string, string | undefined>;
  /** Slots left behind their parent by a run that declined the cascade. */
  stale_keys: string[];
  /** Slots a targeted run may currently name — false only when the family is incomplete. */
  targetable: Record<string, boolean>;
};

/** The brand's live logo family, straight from casino-creation's own source of truth. */
export const fetchLogoFamily = async (tenantId: number, signal?: AbortSignal): Promise<LogoFamily> => {
  const response = await axios.get<LogoFamily>(
    casinoCreation.url(`/casino/tenant/${tenantId}/logo-family`, tenantId),
    casinoCreation.requestConfig(signal),
  );
  return response.data;
};

/* ── 3b. follow the run ──────────────────────────────────────────────────────── */

/** Pipeline states that mean the run is over, whichever way it went. */
const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export type RunStatus = { status: string; settled: boolean; failed: boolean; error?: string };

/**
 * Where one queued run has got to.
 *
 * A logo run is essentially a single step, so there is no useful progress to render — only
 * "still going" versus "over". `settled` is what the caller waits on; `failed` distinguishes
 * a run that ended without replacing anything from one that worked.
 */
export const fetchRunStatus = async (
  pipelineId: string,
  tenantId: number,
  signal?: AbortSignal,
): Promise<RunStatus> => {
  const response = await axios.get<{ status?: string; error?: string }>(
    casinoCreation.url(`/casino/status/${pipelineId}`, tenantId),
    casinoCreation.requestConfig(signal),
  );
  const status = response.data?.status ?? 'unknown';
  return {
    status,
    settled: TERMINAL_RUN_STATUSES.has(status),
    failed: status === 'failed' || status === 'cancelled',
    error: response.data?.error,
  };
};

/* ── 4. what a run will actually change ──────────────────────────────────────── */

/** What one run does to the family: which slots it rewrites, and which it strands. */
export type RunImpact = {
  changed: string[];
  stale: string[];
  favicon_changed: boolean;
  model_calls: { ai: number; upload: number };
};

export type LogoGraph = {
  base_key: string;
  favicon_key: string;
  generation_order: string[];
  /** The `provenance` values that mean "the owner's own artwork". */
  owner_upload_methods: string[];
  assets: Record<string, { label: string; parent_keys: string[]; cascade: RunImpact; no_cascade: RunImpact }>;
};

/**
 * The structure and cost of every possible run, precomputed by the pipeline that executes
 * them. Tenant-independent and immutable for a deployment, so it is fetched once.
 *
 * The point of fetching rather than hardcoding: the dependency graph is the backend's, and
 * every past attempt to mirror it on a client drifted out of step with it. Ask it what a
 * run does; never work it out here.
 */
export const fetchLogoGraph = async (tenantId: number, signal?: AbortSignal): Promise<LogoGraph> => {
  const response = await axios.get<LogoGraph>(
    casinoCreation.url('/casino/logo-graph', tenantId),
    casinoCreation.requestConfig(signal),
  );
  return response.data;
};

/** Whether the family's provenance for a slot means the owner supplied that artwork. */
export const isOwnerArtwork = (graph: LogoGraph | undefined, method: string | undefined): boolean =>
  Boolean(method && graph?.owner_upload_methods?.includes(method));

/**
 * Which logos a rebuild seeded from `slot` would redraw, `slot` itself excluded.
 *
 * For a targetable variant the pipeline has precomputed this and it is read straight off
 * `assets[slot].cascade.changed`. The BASE logo is the exception, and the reason this
 * function exists: it is the root the whole family is generated from, so replacing it is a
 * rebrand rather than a targeted run (see {@link PRIMARY_SLOT}) and it carries NO `assets`
 * entry at all — asking the map about it answers "nothing", which is how the rebuild
 * question came to be missing from the one logo it matters most for.
 *
 * Its impact is therefore read off the shape the graph DOES publish: every asset whose
 * ancestry reaches the base, walked through `parent_keys`. That is still the backend's own
 * answer — no dependency is hardcoded here, and a graph that grows a slot or re-parents one
 * is followed automatically. (The favicon is excluded by the same rule: it declares no
 * parents and is reported separately through `favicon_changed`.)
 */
export const rebuiltBy = (graph: LogoGraph | undefined, slot: string): string[] => {
  // `assets` is typed required but arrives unvalidated from the network, and every caller
  // here runs during render — a 200 with an unexpected body would take the page down.
  if (!graph?.assets) return [];
  const asset = graph.assets[slot];
  if (asset) return asset.cascade.changed.filter((key) => key !== slot);
  if (slot !== graph.base_key) return [];

  // Descendants by fixpoint: a slot joins once any of its parents has. Iterating rather
  // than recursing keeps it safe against a cycle, and the family is eight entries.
  const reached = new Set<string>([slot]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const [key, entry] of Object.entries(graph.assets)) {
      if (reached.has(key)) continue;
      if (entry?.parent_keys?.some((parent) => reached.has(parent))) {
        reached.add(key);
        grew = true;
      }
    }
  }
  reached.delete(slot);
  // Report in the pipeline's own generation order, so the owner reads the set the way it
  // is built rather than in whatever order the map happened to enumerate.
  return (graph.generation_order ?? []).filter((key) => reached.has(key));
};

/**
 * Whether declining the cascade changes anything for this slot — i.e. whether the owner
 * should be ASKED about it. Four of the family's slots are leaves nothing derives from, so
 * for those the question is noise and the toggle should not be shown at all.
 *
 * The base logo is judged by {@link rebuiltBy} instead of by `no_cascade.stale`, for the
 * reason given there: it has no `assets` entry to read a stale list off. Declining the
 * cascade on a rebrand strands every logo derived from it, which is the entire family.
 */
export const cascadeMatters = (graph: LogoGraph | undefined, slot: string): boolean => {
  const asset = graph?.assets?.[slot];
  if (asset) return asset.no_cascade.stale.length > 0;
  return rebuiltBy(graph, slot).length > 0;
};

/** The slots one run would leave behind, for the confirmation copy. Empty when it strands none. */
export const strandedBy = (graph: LogoGraph | undefined, slot: string, cascade: boolean): string[] =>
  cascade ? [] : graph?.assets?.[slot]?.no_cascade.stale ?? [];

/**
 * Every slot one run will rewrite: what the page shows as "Updating…" while it runs.
 *
 * For a targetable slot that is the precomputed impact of the branch the run takes, PLUS the favicon whenever that
 * branch reports `favicon_changed`. The graph never lists the favicon in `changed`, not even for the favicon's own
 * run (whose `changed` is empty), so reading `changed` alone left the favicon tile looking settled while it was
 * being redrawn, both during its own replacement and during a short-name one (the favicon is drawn from it).
 *
 * The base logo has no `assets` entry (see {@link rebuiltBy}): it is the slot itself and, with the cascade,
 * everything derived from it, plus the favicon when one of those reports redrawing it (the short name does: the
 * favicon is drawn from it). Without the cascade a rebrand redraws nothing the favicon is drawn from.
 */
export const runAffects = (graph: LogoGraph | undefined, slot: string, cascade: boolean): string[] => {
  const asset = graph?.assets?.[slot];
  if (!asset) {
    if (!cascade) return [slot];
    const rebuilt = rebuiltBy(graph, slot);
    const redrawsFavicon = rebuilt.some((key) => graph?.assets?.[key]?.cascade?.favicon_changed);
    return redrawsFavicon ? [slot, ...rebuilt, FAVICON_SLOT] : [slot, ...rebuilt];
  }
  const impact = cascade ? asset.cascade : asset.no_cascade;
  // A targeted run always rewrites the slot it names, whatever its `changed` list says (the favicon's is empty).
  const affected = [slot, ...impact.changed.filter((key) => key !== slot)];
  return impact.favicon_changed && !affected.includes(FAVICON_SLOT) ? [...affected, FAVICON_SLOT] : affected;
};
